import { useState, useEffect } from "react";
import {
  saveSavedGiftsOffline,
  getSavedGiftsOffline,
  cacheSearchResult,
  getCachedSearchResults,
  type OfflineSavedGift,
  type OfflineSearchResult,
} from "@/lib/offlineStorage";

export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return isOnline;
}

export function useOfflineSavedGifts() {
  const [gifts, setGifts] = useState<OfflineSavedGift[]>([]);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!isOnline) {
      getSavedGiftsOffline().then(setGifts);
    }
  }, [isOnline]);

  const syncToOffline = async (onlineGifts: OfflineSavedGift[]) => {
    await saveSavedGiftsOffline(onlineGifts);
  };

  return { offlineGifts: gifts, syncToOffline, isOnline };
}

export function useOfflineSearchResults() {
  const [results, setResults] = useState<OfflineSearchResult[]>([]);
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!isOnline) {
      getCachedSearchResults().then(setResults);
    }
  }, [isOnline]);

  const cacheResult = async (result: OfflineSearchResult) => {
    await cacheSearchResult(result);
  };

  return { offlineResults: results, cacheResult, isOnline };
}
